<script lang="ts">
  import type { HafasDepartureRowJson } from '../../types/HafasDepartureRowJson';
  import { DEFAULT_LINE_COLOR } from '../../lib/mode-colors';
  import { deriveRowView } from '../../lib/delay';

  interface Properties {
    row: HafasDepartureRowJson;
    /** Row index within its parent group — used to build a stable id for aria-controls. */
    index: number;
  }

  const { row, index }: Properties = $props();

  /** GTFS colors are `RRGGBB` hex without the leading `#`; guard against bad feed values. */
  const HEX6 = /^[0-9A-Fa-f]{6}$/;

  function toBadgeColor(rawHex: string | null, fallback: string): string {
    if (rawHex !== null && HEX6.test(rawHex)) return `#${rawHex}`;
    return fallback;
  }

  const badgeBg = $derived(toBadgeColor(row.color, DEFAULT_LINE_COLOR));
  const badgeFg = $derived(toBadgeColor(row.text_color, '#FFFFFF'));

  const view = $derived(deriveRowView(row));

  const detailId = $derived(`departure-detail-${String(index)}-${row.line}-${row.scheduled}`);
  /** Concise label read by screen readers on every render — includes delay so AT users
   *  don't have to expand the row to hear it. */
  const ariaLabel = $derived(
    view.delayView
      ? `${row.line} to ${row.headsign}, ${view.label} (${view.delayView.text}). ${view.hoverTitle}`
      : `${row.line} to ${row.headsign}, ${view.label}. ${view.hoverTitle}`,
  );

  const uiState = $state({ isExpanded: false });
  function toggle(): void {
    uiState.isExpanded = !uiState.isExpanded;
  }
</script>

<!-- One board row: colored line badge, headsign, color-coded time.
     When a delay badge exists, the row becomes a `<button>` so hover (desktop)
     or tap (mobile) reveals the badge inline; otherwise it's a plain div and
     doesn't waste a keyboard tab-stop. -->
{#if view.delayView}
  <button
    type="button"
    class="row"
    class:cancelled={row.cancelled}
    class:expanded={uiState.isExpanded}
    aria-expanded={uiState.isExpanded}
    aria-controls={detailId}
    aria-label={ariaLabel}
    onclick={toggle}
  >
    <span class="line-badge" style:background={badgeBg} style:color={badgeFg}>{row.line}</span>
    <span class="headsign">{row.headsign}</span>
    <span
      class="time"
      class:time-normal={view.timeClass === 'time-normal'}
      class:time-muted={view.timeClass === 'time-muted'}
      class:time-late={view.timeClass === 'time-late'}
      class:time-early={view.timeClass === 'time-early'}
      class:time-cancelled={view.timeClass === 'time-cancelled'}>{view.label}</span
    >
    <span id={detailId} class="delay-detail" aria-hidden="true">{view.delayView.text}</span>
  </button>
{:else}
  <div class="row" class:cancelled={row.cancelled} aria-label={ariaLabel}>
    <span class="line-badge" style:background={badgeBg} style:color={badgeFg}>{row.line}</span>
    <span class="headsign">{row.headsign}</span>
    <span
      class="time"
      class:time-normal={view.timeClass === 'time-normal'}
      class:time-muted={view.timeClass === 'time-muted'}
      class:time-late={view.timeClass === 'time-late'}
      class:time-early={view.timeClass === 'time-early'}
      class:time-cancelled={view.timeClass === 'time-cancelled'}>{view.label}</span
    >
  </div>
{/if}

<style>
  .row {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    align-items: center;
    gap: var(--space-sm);
    padding: 4px 0;
    background: transparent;
    border: 0;
    color: inherit;
    font: inherit;
    font-size: var(--font-size-sm);
    text-align: left;
    width: 100%;
  }

  button.row {
    cursor: pointer;
  }

  button.row:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }

  .row.cancelled {
    opacity: 0.65;
  }

  .row.cancelled .line-badge,
  .row.cancelled .headsign,
  .row.cancelled .time {
    text-decoration: line-through;
  }

  .line-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: var(--font-size-xs);
    font-weight: 600;
    min-width: 32px;
    text-align: center;
  }

  .headsign {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .time {
    font-variant-numeric: tabular-nums;
  }

  .time-normal {
    color: var(--color-fg);
  }

  .time-muted {
    color: var(--color-fg-muted);
  }

  .time-late {
    color: var(--color-warn);
  }

  .time-early {
    color: var(--color-accent-green);
  }

  .time-cancelled {
    color: var(--color-accent-red);
  }

  .delay-detail {
    font-size: var(--font-size-xs);
    color: var(--color-fg-muted);
    font-variant-numeric: tabular-nums;
    display: none;
  }

  @media (hover: hover) {
    button.row:hover .delay-detail {
      display: inline;
    }
  }

  button.row.expanded .delay-detail {
    display: inline;
  }
</style>
